import { Checkbox, Tooltip, type CheckboxChangeEvent } from 'antd';
import DisplayTrapNameId from '@features/regional-overview/digital-traps/components/display-trap-name-id';
import StatusTag from '@features/regional-overview/digital-traps/components/status-tag/status-tag.component';
import STTypo, { STTypoModels } from 'core/shared/typo';
import { COLOR_OPTIONS } from 'config/style';
import { useTranslation } from 'react-i18next';
import LocationCell from '../location-cell';
import { NETWORK_METRICS } from '../network-management.constants';
import type { NetworkTrapRow } from '../network-management.models';
import { isSelectionDisabled } from '../network-management.utils';
import styles from './network-trap-card.module.css';

export interface NetworkTrapCardProps {
  record: NetworkTrapRow;
  checked: boolean;
  onCheckedChange: (record: NetworkTrapRow, checked: boolean) => void;
  selectedPestEppoCode?: string;
}

export const NetworkTrapCard = ({ record, checked, onCheckedChange, selectedPestEppoCode }: NetworkTrapCardProps) => {
  const [t] = useTranslation();
  const isCheckboxDisabled = isSelectionDisabled(record, selectedPestEppoCode);

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    onCheckedChange(record, event.target.checked);
  };

  const selectionCheckbox = <Checkbox checked={checked} disabled={isCheckboxDisabled} onChange={handleCheckboxChange} />;

  return (
    <article className={styles.cardContainer}>
      <header className={styles.cardHeader}>
        {isCheckboxDisabled ? (
          <Tooltip title={t('pages.regional_overview.digital_traps.network_management.selection_disabled_tooltip')}>
            {selectionCheckbox}
          </Tooltip>
        ) : (
          selectionCheckbox
        )}
        <div className={styles.trapIdentificationWrapper}>
          <DisplayTrapNameId trapName={record.name} trapId={record.id} />
        </div>
        <div className={styles.locationPill}>
          <LocationCell record={record} />
        </div>
      </header>
      <section className={styles.metricsSection}>
        {NETWORK_METRICS.map(metric => (
          <div className={styles.metricCell} key={metric.key}>
            <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
              {t(metric.labelKey)}
            </STTypo>
            <StatusTag type={metric.statusType} status={record[metric.key]} alignSelf='center' positiveStatus={metric.positiveStatus} />
          </div>
        ))}
      </section>
    </article>
  );
};
